import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { X } from "lucide-react";
import { employeeCodeStructureService } from "../../../services/api";
import DatePicker from "../../Common/DatePicker";

// Employee Code Structure Component
const EmployeeCodeStructure = ({ darkMode, branches, departments }) => {
  const [organizationLevel, setOrganizationLevel] = useState("company");
  const [companyConfig, setCompanyConfig] = useState({
    prefix: "NG",
    digits: 5,
    startingNumber: 1,
    autoReset: "never",
  });
  const [branchConfigs, setBranchConfigs] = useState({});
  const [departmentConfigs, setDepartmentConfigs] = useState({});
  const [effectiveDate, setEffectiveDate] = useState("");
  const [sharedNumberSequence, setSharedNumberSequence] = useState(false);
  const [showNewEntitiesModal, setShowNewEntitiesModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Load saved configurations from API
  useEffect(() => {
    const loadEmployeeCodeStructure = async () => {
      try {
        const data = await employeeCodeStructureService.getCurrentStructure();
        const structure = data.data;

        if (structure) {
          // Set pattern type based on database
          if (structure.patternType === "automated") {
            setCompanyConfig({
              effectiveDate: structure.effectiveDate || "",
              prefix: structure.prefix || "NG",
              digits: structure.numberOfDigits || 5,
              startingNumber: structure.startingNumber || 1,
              autoReset: structure.autoReset || "never",
            });
          }
          // Note: For manual pattern, we don't need to set any specific config
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // No structure found - this is normal for new companies
          console.log("No employee code structure found - using defaults");
        } else {
          console.error("Error loading employee code structure:", error);
        }
      }
    };

    loadEmployeeCodeStructure();
  }, []);

  // Save configuration to API
  const saveConfiguration = async () => {
    try {
      // For now, we'll only support company-level automated configuration
      // Branch and department level configurations can be added later

      // Validate required fields
      if (!effectiveDate) {
        toast.error("Please select an effective date");
        return;
      }

      // Validate company configuration for automated pattern
      if (organizationLevel === "company") {
        if (
          !companyConfig.prefix ||
          !companyConfig.digits ||
          !companyConfig.startingNumber
        ) {
          toast.error(
            "Please fill in all required company configuration fields"
          );
          return;
        }
      } else {
        toast.error(
          "Branch and department level configurations are not yet supported. Please use company level."
        );
        return;
      }

      // Prepare API payload
      const payload = {
        patternType: "automated", // For now, we only support automated for company level
        prefix: companyConfig.prefix,
        numberOfDigits: companyConfig.digits,
        startingNumber: companyConfig.startingNumber,
        autoReset: companyConfig.autoReset,
        effectiveDate: effectiveDate,
      };

      console.log("[Save Config] Sending payload to backend:", payload);

      // Call API to save configuration
      const data = await employeeCodeStructureService.saveStructure(payload);
      console.log("Configuration saved successfully:", data);
      console.log(
        "[Save Config] Response from backend:",
        JSON.stringify(data, null, 2)
      );

      if (data.success === false) {
        toast.error("Failed to save configuration: " + data.message);
        return;
      }

      // Clear form
      setEffectiveDate("");

      // Show success message
      toast.success("Employee code structure saved successfully!");

      // Close any modals if they exist
      setShowNewEntitiesModal(false);
      setShowPreviewModal(false);
    } catch (error) {
      console.error("Error saving configuration:", error);
      console.error("[Save Config] Error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data,
      });
      // Check if it's a consecutive duplicate error
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes(
          "same configuration consecutively"
        )
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to save configuration");
      }
    }
  };

  // Generate preview codes for current configuration
  const generatePreviewCodes = () => {
    const codes = [];

    if (organizationLevel === "company") {
      for (let i = 1; i <= 5; i++) {
        const number = (companyConfig.startingNumber + i - 1)
          .toString()
          .padStart(companyConfig.digits, "0");
        codes.push(`${companyConfig.prefix}${number}`);
      }
    } else if (organizationLevel === "branch") {
      if (sharedNumberSequence) {
        // For shared number sequence, use global sequence across all branches
        const globalStartingNumber =
          Object.values(branchConfigs)[0]?.startingNumber || 1;
        const globalDigits = Object.values(branchConfigs)[0]?.digits || 5;
        let currentNumber = globalStartingNumber;

        Object.keys(branchConfigs).forEach((branchId) => {
          const branch = branches.find((b) => b.id === parseInt(branchId));
          const config = branchConfigs[branchId];
          if (branch && config) {
            for (let i = 1; i <= 3; i++) {
              const number = currentNumber
                .toString()
                .padStart(globalDigits, "0");
              codes.push(`${config.prefix}${number}`);
              currentNumber++;
            }
          }
        });
      } else {
        // Individual number sequences for each branch
        Object.keys(branchConfigs).forEach((branchId) => {
          const branch = branches.find((b) => b.id === parseInt(branchId));
          const config = branchConfigs[branchId];
          if (branch && config) {
            for (let i = 1; i <= 3; i++) {
              const number = (config.startingNumber + i - 1)
                .toString()
                .padStart(config.digits, "0");
              codes.push(`${config.prefix}${number}`);
            }
          }
        });
      }
    } else if (organizationLevel === "department") {
      if (sharedNumberSequence) {
        // For shared number sequence, use global sequence across all departments
        const globalStartingNumber =
          Object.values(departmentConfigs)[0]?.startingNumber || 1;
        const globalDigits = Object.values(departmentConfigs)[0]?.digits || 5;
        let currentNumber = globalStartingNumber;

        Object.keys(departmentConfigs).forEach((deptId) => {
          const dept = departments.find((d) => d.id === parseInt(deptId));
          const config = departmentConfigs[deptId];
          if (dept && config) {
            for (let i = 1; i <= 3; i++) {
              const number = currentNumber
                .toString()
                .padStart(globalDigits, "0");
              codes.push(`${config.prefix}${number}`);
              currentNumber++;
            }
          }
        });
      } else {
        // Individual number sequences for each department
        Object.keys(departmentConfigs).forEach((deptId) => {
          const dept = departments.find((d) => d.id === parseInt(deptId));
          const config = departmentConfigs[deptId];
          if (dept && config) {
            for (let i = 1; i <= 3; i++) {
              const number = (config.startingNumber + i - 1)
                .toString()
                .padStart(config.digits, "0");
              codes.push(`${config.prefix}${number}`);
            }
          }
        });
      }
    }

    return codes;
  };

  // Get tomorrow's date for effective date validation (IST)
  const getTomorrowDate = () => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    const tomorrow = new Date(istTime);
    tomorrow.setDate(tomorrow.getDate());
    return tomorrow.toISOString().split("T")[0];
  };

  // Get today's date for effective date validation (IST)
  const getTodayDate = () => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    return istTime.toISOString().split("T")[0];
  };

  // Handle branch configuration change
  const handleBranchConfigChange = (branchId, field, value) => {
    setBranchConfigs((prev) => ({
      ...prev,
      [branchId]: {
        ...prev[branchId],
        [field]: value,
      },
    }));
  };

  // Handle department configuration change
  const handleDepartmentConfigChange = (deptId, field, value) => {
    setDepartmentConfigs((prev) => ({
      ...prev,
      [deptId]: {
        ...prev[deptId],
        [field]: value,
      },
    }));
  };

  // Handle shared number sequence change
  const handleSharedNumberSequenceChange = (checked) => {
    setSharedNumberSequence(checked);
    if (checked) {
      if (organizationLevel === "branch") {
        // Apply same starting number to all branches
        const currentStartingNumber =
          Object.values(branchConfigs)[0]?.startingNumber || 1;
        const updatedConfigs = {};
        branches.forEach((branch) => {
          updatedConfigs[branch.id] = {
            ...branchConfigs[branch.id],
            startingNumber: currentStartingNumber,
          };
        });
        setBranchConfigs(updatedConfigs);
      } else if (organizationLevel === "department") {
        // Apply same starting number to all departments
        const currentStartingNumber =
          Object.values(departmentConfigs)[0]?.startingNumber || 1;
        const updatedConfigs = {};
        departments.forEach((dept) => {
          updatedConfigs[dept.id] = {
            ...departmentConfigs[dept.id],
            startingNumber: currentStartingNumber,
          };
        });
        setDepartmentConfigs(updatedConfigs);
      }
    }
  };

  // Handle new branches/departments
  const handleNewEntities = () => {
    if (organizationLevel === "branch") {
      const newBranches = branches.filter(
        (branch) => !branchConfigs[branch.id]
      );
      if (newBranches.length > 0) {
        const updatedConfigs = { ...branchConfigs };
        newBranches.forEach((branch) => {
          updatedConfigs[branch.id] = {
            prefix: branch.name.substring(0, 2).toUpperCase(),
            digits: 5,
            startingNumber: sharedNumberSequence
              ? Object.values(branchConfigs)[0]?.startingNumber || 1
              : 1,
          };
        });
        setBranchConfigs(updatedConfigs);
        toast.success(
          `${newBranches.length} new branch(es) added to configuration`
        );
      }
    } else if (organizationLevel === "department") {
      const newDepartments = departments.filter(
        (dept) => !departmentConfigs[dept.id]
      );
      if (newDepartments.length > 0) {
        const updatedConfigs = { ...departmentConfigs };
        newDepartments.forEach((dept) => {
          updatedConfigs[dept.id] = {
            prefix: `NG${dept.name.substring(0, 2).toUpperCase()}`,
            digits: 5,
            startingNumber: sharedNumberSequence
              ? Object.values(departmentConfigs)[0]?.startingNumber || 1
              : 1,
          };
        });
        setDepartmentConfigs(updatedConfigs);
        toast.success(
          `${newDepartments.length} new department(s) added to configuration`
        );
      }
    }
    setShowNewEntitiesModal(false);
  };

  return (
    <div
      className={`p-4 rounded-none ${
        darkMode ? "bg-gray-800" : "bg-white"
      } shadow-md`}
    >
      {/* Organization Level Selection */}
      <div
        className={`p-3 rounded-none ${
          darkMode ? "bg-gray-700" : "bg-gray-50"
        } mb-4`}
      >
        <h3
          className={`text-base font-semibold mb-2 ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Organization Level
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {/* {['company', 'branch', 'department'].map(level => ( */}
          {["company"].map((level) => (
            <button
              key={level}
              onClick={() => setOrganizationLevel(level)}
              className={`p-2 rounded-none border-2 transition-all duration-200 ${
                organizationLevel === level
                  ? `${
                      darkMode
                        ? "border-redhalo-red rh-btn-primary"
                        : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy"
                    }`
                  : `${
                      darkMode
                        ? "border-gray-600 bg-gray-600 text-gray-300 hover:border-gray-500"
                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                    }`
              }`}
            >
              <div className="text-center">
                <div className="text-sm font-semibold capitalize">{level}</div>
                <div className="text-xs mt-1">
                  {level === "company" && "Single pattern for all employees"}
                  {level === "branch" && "Different pattern per branch"}
                  {level === "department" && "Different pattern per department"}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Global Effective Date Section */}
      <div
        className={`p-3 rounded-none ${
          darkMode ? "bg-gray-700" : "bg-gray-50"
        } mb-4`}
      >
        <h3
          className={`text-base font-semibold mb-3 ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Effective Date Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label
              className={`block text-xs font-medium mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Effective Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={companyConfig.effectiveDate}
              min={getTomorrowDate()}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = getTodayDate();
                if (selectedDate < today) {
                  toast.error(
                    "Please select a future date (tomorrow or later)"
                  );
                  return;
                }
                setEffectiveDate(selectedDate);
              }}
              className={`w-full p-2 border rounded text-sm ${
                darkMode
                  ? "bg-gray-600 border-gray-500 text-white"
                  : "bg-white border-gray-300"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Only future dates (tomorrow or later) are allowed to prevent
              database conflicts
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      <div
        className={`p-3 rounded-none ${
          darkMode ? "bg-gray-700" : "bg-gray-50"
        } mb-4`}
      >
        <h3
          className={`text-base font-semibold mb-3 ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          {organizationLevel === "company" && "Company Level Configuration"}
          {organizationLevel === "branch" && "Branch Level Configuration"}
          {organizationLevel === "department" &&
            "Department Level Configuration"}
        </h3>

        {organizationLevel === "company" && (
          <div className="space-y-3">
            {/* Configuration Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Company Prefix
                </label>
                <input
                  type="text"
                  value={companyConfig.prefix}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      prefix: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g., NG, ABC, XYZ"
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Number of Digits
                </label>
                <select
                  value={companyConfig.digits}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      digits: parseInt(e.target.value),
                    }))
                  }
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <option value={3}>3 digits (001-999)</option>
                  <option value={4}>4 digits (0001-9999)</option>
                  <option value={5}>5 digits (00001-99999)</option>
                  <option value={6}>6 digits (000001-999999)</option>
                  <option value={7}>7 digits (0000001-9999999)</option>
                  <option value={8}>8 digits (00000001-99999999)</option>
                  <option value="custom">Custom (Enter manually)</option>
                </select>
                {companyConfig.digits === "custom" && (
                  <input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="Enter number of digits (1-10)"
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value >= 1 && value <= 10) {
                        setCompanyConfig((prev) => ({
                          ...prev,
                          digits: value,
                        }));
                      }
                    }}
                    className={`w-full p-2 border rounded mt-1 text-sm ${
                      darkMode
                        ? "bg-gray-600 border-gray-500 text-white"
                        : "bg-white border-gray-300"
                    }`}
                  />
                )}
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Starting Number
                </label>
                <input
                  type="number"
                  value={companyConfig.startingNumber}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      startingNumber: parseInt(e.target.value),
                    }))
                  }
                  placeholder="e.g., 1"
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Auto Reset
                </label>
                <select
                  value={companyConfig.autoReset}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      autoReset: e.target.value,
                    }))
                  }
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <option value="never">Never</option>
                  <option value="yearly">Yearly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {organizationLevel === "branch" && (
          <div className="space-y-3">
            {/* Shared Number Sequence Option */}
            <div
              className={`p-3 rounded-none border ${
                darkMode
                  ? "border-gray-600 bg-gray-600"
                  : "border-gray-300 bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4
                    className={`font-semibold mb-1 ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    Shared Number Sequence
                  </h4>
                  <p
                    className={`text-xs ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Use the same number sequence across all branches (only
                    prefix changes)
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sharedNumberSequence}
                    onChange={(e) =>
                      handleSharedNumberSequenceChange(e.target.checked)
                    }
                    className="sr-only"
                  />
                  <div
                    className={`relative w-8 h-5 transition-colors duration-200 ease-in-out rounded-full ${
                      sharedNumberSequence
                        ? darkMode
                          ? "bg-redhalo-red"
                          : "bg-redhalo-mint/400"
                        : darkMode
                        ? "bg-gray-600"
                        : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute w-3 h-3 bg-white rounded-full transition-transform duration-200 ease-in-out transform ${
                        sharedNumberSequence
                          ? "translate-x-4"
                          : "translate-x-0.5"
                      } top-1`}
                    ></div>
                  </div>
                </label>
              </div>
            </div>

            {/* New Branches Notification */}
            {branches.filter((branch) => !branchConfigs[branch.id]).length >
              0 && (
              <div
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-yellow-600 bg-yellow-900"
                    : "border-yellow-300 bg-yellow-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        darkMode ? "text-yellow-200" : "text-yellow-800"
                      }`}
                    >
                      New Branches Detected
                    </h4>
                    <p
                      className={`text-xs ${
                        darkMode ? "text-yellow-300" : "text-yellow-700"
                      }`}
                    >
                      {
                        branches.filter((branch) => !branchConfigs[branch.id])
                          .length
                      }{" "}
                      new branch(es) found. Click "Add New Branches" to include
                      them in the configuration.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewEntitiesModal(true)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode
                        ? "bg-yellow-600 text-white hover:bg-yellow-700"
                        : "bg-yellow-500 text-white hover:bg-yellow-600"
                    } transition-colors`}
                  >
                    Add New Branches
                  </button>
                </div>
              </div>
            )}

            {branches.map((branch) => (
              <div
                key={branch.id}
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-gray-600 bg-gray-600"
                    : "border-gray-300 bg-white"
                }`}
              >
                <h4
                  className={`font-semibold mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  {branch.name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={branchConfigs[branch.id]?.prefix || ""}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "prefix",
                          e.target.value.toUpperCase()
                        )
                      }
                      placeholder={`e.g., ${branch.name
                        .substring(0, 2)
                        .toUpperCase()}`}
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Number of Digits
                    </label>
                    <select
                      value={branchConfigs[branch.id]?.digits || 5}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "digits",
                          parseInt(e.target.value)
                        )
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <option value={3}>3 digits</option>
                      <option value={4}>4 digits</option>
                      <option value={5}>5 digits</option>
                      <option value={6}>6 digits</option>
                      <option value={7}>7 digits</option>
                      <option value={8}>8 digits</option>
                      <option value="custom">Custom</option>
                    </select>
                    {branchConfigs[branch.id]?.digits === "custom" && (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Enter number of digits (1-10)"
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= 1 && value <= 10) {
                            handleBranchConfigChange(
                              branch.id,
                              "digits",
                              value
                            );
                          }
                        }}
                        className={`w-full p-2 border rounded mt-1 text-sm ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "bg-white border-gray-300"
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Starting Number
                      {sharedNumberSequence && (
                        <span
                          className={`ml-1 text-xs ${
                            darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                          }`}
                        >
                          (Shared)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={branchConfigs[branch.id]?.startingNumber || 1}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "startingNumber",
                          parseInt(e.target.value)
                        )
                      }
                      placeholder="e.g., 1"
                      disabled={
                        sharedNumberSequence && branch.id !== branches[0]?.id
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      } ${
                        sharedNumberSequence && branch.id !== branches[0]?.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {organizationLevel === "department" && (
          <div className="space-y-3">
            {/* Shared Number Sequence Option */}
            <div
              className={`p-3 rounded-none border ${
                darkMode
                  ? "border-gray-600 bg-gray-600"
                  : "border-gray-300 bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4
                    className={`font-semibold mb-1 ${
                      darkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    Shared Number Sequence
                  </h4>
                  <p
                    className={`text-xs ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Use the same number sequence across all departments (only
                    prefix changes)
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sharedNumberSequence}
                    onChange={(e) =>
                      handleSharedNumberSequenceChange(e.target.checked)
                    }
                    className="sr-only"
                  />
                  <div
                    className={`relative w-8 h-5 transition-colors duration-200 ease-in-out rounded-full ${
                      sharedNumberSequence
                        ? darkMode
                          ? "bg-redhalo-red"
                          : "bg-redhalo-mint/400"
                        : darkMode
                        ? "bg-gray-600"
                        : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute w-3 h-3 bg-white rounded-full transition-transform duration-200 ease-in-out transform ${
                        sharedNumberSequence
                          ? "translate-x-4"
                          : "translate-x-0.5"
                      } top-1`}
                    ></div>
                  </div>
                </label>
              </div>
            </div>

            {/* New Departments Notification */}
            {departments.filter((dept) => !departmentConfigs[dept.id]).length >
              0 && (
              <div
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-yellow-600 bg-yellow-900"
                    : "border-yellow-300 bg-yellow-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        darkMode ? "text-yellow-200" : "text-yellow-800"
                      }`}
                    >
                      New Departments Detected
                    </h4>
                    <p
                      className={`text-xs ${
                        darkMode ? "text-yellow-300" : "text-yellow-700"
                      }`}
                    >
                      {
                        departments.filter(
                          (dept) => !departmentConfigs[dept.id]
                        ).length
                      }{" "}
                      new department(s) found. Click "Add New Departments" to
                      include them in the configuration.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewEntitiesModal(true)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode
                        ? "bg-yellow-600 text-white hover:bg-yellow-700"
                        : "bg-yellow-500 text-white hover:bg-yellow-600"
                    } transition-colors`}
                  >
                    Add New Departments
                  </button>
                </div>
              </div>
            )}

            {departments.map((dept) => (
              <div
                key={dept.id}
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-gray-600 bg-gray-600"
                    : "border-gray-300 bg-white"
                }`}
              >
                <h4
                  className={`font-semibold mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  {dept.name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={departmentConfigs[dept.id]?.prefix || ""}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "prefix",
                          e.target.value.toUpperCase()
                        )
                      }
                      placeholder={`e.g., NG${dept.name
                        .substring(0, 2)
                        .toUpperCase()}`}
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Number of Digits
                    </label>
                    <select
                      value={departmentConfigs[dept.id]?.digits || 5}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "digits",
                          parseInt(e.target.value)
                        )
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <option value={3}>3 digits</option>
                      <option value={4}>4 digits</option>
                      <option value={5}>5 digits</option>
                      <option value={6}>6 digits</option>
                      <option value={7}>7 digits</option>
                      <option value={8}>8 digits</option>
                      <option value="custom">Custom</option>
                    </select>
                    {departmentConfigs[dept.id]?.digits === "custom" && (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Enter number of digits (1-10)"
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= 1 && value <= 10) {
                            handleDepartmentConfigChange(
                              dept.id,
                              "digits",
                              value
                            );
                          }
                        }}
                        className={`w-full p-2 border rounded mt-1 text-sm ${
                          darkMode
                            ? "bg-gray-600 border-gray-500 text-white"
                            : "bg-white border-gray-300"
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Starting Number
                      {sharedNumberSequence && (
                        <span
                          className={`ml-1 text-xs ${
                            darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                          }`}
                        >
                          (Shared)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={departmentConfigs[dept.id]?.startingNumber || 1}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "startingNumber",
                          parseInt(e.target.value)
                        )
                      }
                      placeholder="e.g., 1"
                      disabled={
                        sharedNumberSequence && dept.id !== departments[0]?.id
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : "bg-white border-gray-300"
                      } ${
                        sharedNumberSequence && dept.id !== departments[0]?.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Configuration Buttons */}
      <div
        className={`p-3 rounded-none ${
          darkMode ? "bg-gray-700" : "bg-gray-50"
        } mb-4`}
      >
        <div className="flex justify-between items-center">
          <button
            onClick={() => setShowPreviewModal(true)}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-green-500 text-white hover:bg-green-600"
            } transition-colors`}
          >
            Live Preview
          </button>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                // Reset to current organization level, not always company
                setCompanyConfig({
                  prefix: "NG",
                  digits: 5,
                  startingNumber: 1,
                  autoReset: "never",
                });
                setBranchConfigs({});
                setDepartmentConfigs({});
                setEffectiveDate("");
                setSharedNumberSequence(false);
                toast.success("Configuration reset successfully!");
              }}
              className={`px-4 py-2 rounded text-sm font-medium ${
                darkMode
                  ? "bg-gray-600 text-white hover:bg-gray-500"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              } transition-colors`}
            >
              Reset
            </button>
            <button
              onClick={saveConfiguration}
              disabled={!effectiveDate}
              className={`px-4 py-2 rounded text-sm font-medium ${
                !effectiveDate
                  ? `${
                      darkMode
                        ? "bg-gray-600 text-gray-400"
                        : "bg-gray-300 text-gray-500"
                    } cursor-not-allowed`
                  : `${
                      darkMode
                        ? "rh-btn-primary hover:opacity-95"
                        : "rh-btn-primary"
                    }`
              } transition-colors`}
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>

      {/* Live Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            className={`p-6 rounded-none shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-lg font-semibold ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Live Preview -{" "}
                {organizationLevel.charAt(0).toUpperCase() +
                  organizationLevel.slice(1)}{" "}
                Level
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div
              className={`p-4 rounded ${
                darkMode ? "bg-gray-700" : "bg-gray-100"
              }`}
            >
              <div className="text-center">
                <p
                  className={`text-sm mb-3 ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Sample generated codes for {organizationLevel} level:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {generatePreviewCodes().map((code, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded ${
                        darkMode ? "bg-gray-600" : "bg-white"
                      } border ${
                        darkMode ? "border-gray-500" : "border-gray-200"
                      }`}
                    >
                      <p
                        className={`font-mono text-lg ${
                          darkMode ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {code}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Entities Modal */}
      {showNewEntitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            className={`p-6 rounded-none shadow-lg max-w-md w-full mx-4 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Add New{" "}
              {organizationLevel === "branch" ? "Branches" : "Departments"}
            </h3>
            <p
              className={`text-sm mb-4 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {organizationLevel === "branch"
                ? `${
                    branches.filter((branch) => !branchConfigs[branch.id])
                      .length
                  } new branch(es) will be added with default configurations.`
                : `${
                    departments.filter((dept) => !departmentConfigs[dept.id])
                      .length
                  } new department(s) will be added with default configurations.`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNewEntitiesModal(false)}
                className={`px-4 py-2 rounded-none text-sm font-medium ${
                  darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                } transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleNewEntities}
                className={`px-4 py-2 rounded-none text-sm font-medium ${
                  darkMode
                    ? "rh-btn-primary hover:opacity-95"
                    : "rh-btn-primary"
                } transition-colors`}
              >
                Add{" "}
                {organizationLevel === "branch" ? "Branches" : "Departments"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Preview Modal */}
    </div>
  );
};

export default EmployeeCodeStructure;
